import React, { FC, JSX, useEffect, useState } from 'react';
import { Typography } from '@components/atoms/Typography';
import styles from './Notification.module.css';

export interface NotificationProps {
    message: string;
    product?: NotificationProduct | NotificationProduct[];
    size: 'full-width' | 'md' | 'sm';
    type: 'success' | 'error';
    endIcon?: string;
    dismissible?: boolean;
    position?: {
        top?: string;
        bottom?: string;
        align?: 'left' | 'center' | 'right';
    };
    autoDismiss?: number; // in seconds
    borderRadiusStyle: 'rounded' | 'not-rounded';
    onClose?: () => void;
    children?: JSX.Element | JSX.Element[];
}

interface NotificationProduct {
    name: string;
    image: string;
    quantity?: number;
}

export const Notification: FC<NotificationProps> = ({
    message,
    product,
    size,
    type,
    endIcon,
    dismissible,
    position,
    autoDismiss,
    borderRadiusStyle,
    onClose,
    children,
}) => {
    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
        if (autoDismiss) {
            setTimeout(() => {
                setIsClosing(true);
            }, autoDismiss * 1000);
        }
    }, [autoDismiss]);

    useEffect(() => {
        if (isClosing && onClose) {
            setTimeout(() => onClose(), 400);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isClosing]);

    const [distanceFromWindow, setDistanceFromWindow] = useState(0);

    useEffect(() => {
        const handleResize = () => {
            const distance = (document.querySelector('#header > .container > div') as HTMLDivElement)?.offsetLeft;
            setDistanceFromWindow(distance);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div
            className={
                `${position ? styles['fixed-container'] : ''}` +
                ` ${position?.align ? styles[`fixed-container-${position.align}`] : ''}` +
                ` ${position?.top ? styles['fixed-container-top'] : ''}` +
                ` ${position?.bottom ? styles['fixed-container-bottom'] : ''}` +
                ` ${isClosing ? styles['slide-out'] : position?.bottom ? styles['slide-in'] : styles['slide-out']}`
            }
            style={
                position
                    ? {
                          top: position.top,
                          bottom: position.bottom,
                          right: position.align === 'right' ? `${distanceFromWindow}px` : undefined,
                          left: position.align === 'left' ? `${distanceFromWindow}px` : undefined,
                          margin: position.align === 'center' ? '0 auto' : undefined,
                      }
                    : {}
            }>
            <div
                className={
                    `${styles['alert-container']}` +
                    ` ${size && Array.isArray(product) && product.length > 1 ? styles[`alert-container-bundle-${size}`] : styles[`alert-container-${size}`]}` +
                    ` ${styles[`alert-container-${type}`]}` +
                    // border radius
                    ` ${borderRadiusStyle === 'rounded' ? styles['rounded-border-radius'] : ''}`
                }>
                <div className={`${styles['alert-body']} d-flex justify-content-between gap-3`}>
                    <div className="d-flex align-items-center justify-content-between gap-3 flex-grow-1">
                        <div className="d-flex align-items-center gap-3">
                            <div>
                                <div className={styles['round-icon']}>
                                    <i className={`fa-light ${type === 'success' ? 'fa-circle-check' : 'fa-circle-xmark'}`} />
                                </div>
                            </div>
                            <div className="d-flex flex-column">
                                <Typography fontSize="16">{message}</Typography>
                                {children}
                            </div>
                        </div>
                        {endIcon && (
                            <Typography fontSize="16">
                                <i className={endIcon} />
                            </Typography>
                        )}
                    </div>
                    {dismissible && (
                        <div>
                            <button type="button" onClick={() => setIsClosing(true)}>
                                <i className="fa-thin fa-xmark" style={{ fontSize: '28px' }}></i>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
